import { PaymentModuleId } from '@foundation/apis/payments';
import Clarity from '@microsoft/clarity';
import { Theme, useTheme } from '@pmi.web/react-theme';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import cn from 'clsx';
import { useEffect, useRef, useState, JSX } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  disableUIBlocker,
  enableUIBlocker,
  LoadingSpinner,
  LoadingSpinnerSize,
} from '@/components/common';
import { AlertWarning } from '@/components/common/alerts/AlertWarning';
import { RedirectStatus } from '@/components/contexts/params';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { triggerErrorToast, triggerInfoToast } from '@/libs/toasts';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import NuveiCardService from '@/services/nuvei/NuveiCardService';
import { NuveiCardFieldValidationEvent } from '@/services/nuvei/NuveiCreatePaymentInput';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { getQaTestingProps, QaTestingElements } from '@/utils/qaTesting';

import './NuveiCardForm.css';

interface INuveiCardFormProps {
  navigation?: JSX.Element | null;
}

export function NuveiCardForm({ navigation }: INuveiCardFormProps) {
  const { theme } = useTheme();
  const { accessToken, userId } = useAuth();
  const cardFieldDivRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nuveiCardFieldRef = useRef<any>(null);
  const { t } = useTranslation();
  const paymentModuleT = useUserFriendlyPaymentModuleName();
  const [cardHolderName, setCardHolderName] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const [isCardHolderFieldValid, setIsCardHolderFieldValid] = useState(false);
  const [isCardNumberFieldComplete, setIsCardNumberFieldComplete] =
    useState(false);

  useEffect(() => {
    if (cardHolderName?.trim()?.length <= 0) {
      setIsCardHolderFieldValid(false);
    } else {
      setIsCardHolderFieldValid(true);
    }
  }, [cardHolderName]);

  useEffect(() => {
    setLoading(true);

    const promise = NuveiCardService.initTransactionAndSafeCharge(
      undefined,
      userId,
      accessToken ?? '',
    );
    promise
      .then((safeCharge) => {
        const safeChargeFields = safeCharge.fields({
          fonts: [
            {
              //PM's cdn with css file that contains needed fonts used in Braintree - this is needed to make Nuvei hosted files look close to Braintree
              cssUrl: `${import.meta.env.VITE_PUBLIC_CDN_URL ?? ''}/${
                theme === Theme.FitLine
                  ? import.meta.env.VITE_CDN_PATH_FITLINE_FONT
                  : import.meta.env.VITE_CDN_PATH_PM_FONT
              }`,
            },
          ],
          locale: 'en',
        });

        if (nuveiCardFieldRef.current !== null) return;

        nuveiCardFieldRef.current = safeChargeFields.create('card', {
          style: {
            base: {
              fontFamily: theme === Theme.FitLine ? 'Inter' : 'uni-neue',
            },
          },
        });

        nuveiCardFieldRef.current.attach('#card-field');
        nuveiCardFieldRef.current.on(
          'change',
          (event: NuveiCardFieldValidationEvent) => {
            if (event?.error?.type === 'validation_error') {
              if (cardFieldDivRef.current) {
                cardFieldDivRef.current?.classList?.remove(
                  'border-transparent',
                );
                cardFieldDivRef.current?.classList?.add('border-error');
              }
            } else {
              cardFieldDivRef.current?.classList?.remove('border-error');
              cardFieldDivRef.current?.classList?.add('border-transparent');
            }

            if (event?.complete) {
              setIsCardNumberFieldComplete(true);
            } else {
              setIsCardNumberFieldComplete(false);
            }
          },
        );
      })
      .catch((err) => {
        TelemetryService.trackException(err, 'NuveiCardForm');
        Logger.error(
          LoggerOrigin.ReactComponent,
          'NuveiCardForm initialization failure',
          err,
        );
        setError(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const submitCardForm = async (
    cardNumber: unknown,
    cardHolderName: string,
  ) => {
    const currentPage = NavigationService.getInternalPage();
    enableUIBlocker();

    try {
      await NuveiCardService.createPayment(cardNumber, cardHolderName);

      if (PaymentStore.flow === PaymentIntentFlow.Manage) {
        searchParams.set('status', RedirectStatus.Success);
        setSearchParams(searchParams);
        window.location.reload();
      } else {
        NavigationService.navigateTo({ page: InternalPage.StatusProcessing });
        await NuveiCardService.confirmPayment(
          PaymentStore.paymentIntentId,
          accessToken ?? '',
        );
        NavigationService.navigateTo({ page: InternalPage.StatusSuccess });
      }

      Clarity.setTag('pay', [
        RedirectStatus.Success,
        paymentModuleT(PaymentModuleId.NuveiCreditCard),
      ]);
    } catch (error) {
      NavigationService.navigateTo(currentPage);

      const paymentError = error as Error;
      TelemetryService.trackException(paymentError, 'NuveiCardForm', {
        pid: PaymentStore.paymentIntentId,
      });
      Logger.error(
        LoggerOrigin.ReactComponent,
        'NuveiCardForm failed to authorize the card.',
        paymentError,
      );

      if (
        NuveiCardService.isNuveiError(paymentError) &&
        NuveiCardService.isNuveiTransactionCancelledByUser(paymentError)
      ) {
        triggerInfoToast(t('Select a new payment method and proceed to pay.'));
        Clarity.setTag('pay', [
          RedirectStatus.Canceled,
          paymentModuleT(PaymentModuleId.NuveiCreditCard),
        ]);
      } else {
        Clarity.setTag('pay', [
          RedirectStatus.Failure,
          paymentModuleT(PaymentModuleId.NuveiCreditCard),
        ]);
        triggerErrorToast(
          t('Something went wrong!'),
          t(
            'Operation failed. Please try again with a different payment method.',
          ),
        );
      }
    } finally {
      disableUIBlocker();
    }
  };

  return (
    <div className="relative">
      {loading && !error && (
        <div className={'absolute w-full h-full flex justify-center'}>
          <LoadingSpinner size={LoadingSpinnerSize.Small} />
        </div>
      )}
      {error && (
        <AlertWarning
          title={t('Something went wrong!')}
          description={t('Card payments are currently unavailable.')}
        />
      )}

      {!error && (
        <div className="bg-white p-sm rounded-md ring-1 ring-silver-700">
          <input
            id="cardHolderName"
            placeholder="Cardholder name"
            type="text"
            value={cardHolderName}
            onChange={(e) => setCardHolderName(e.target.value)}
            className="relative rounded-field bg-secondary h-14 p-sm focused-container w-full shadow-on-focus ring-silver-700 ring-1 focus-visible:ring-silver-700 focus-visible:ring-1"
            {...getQaTestingProps(
              QaTestingElements.CardHolderName,
              cardHolderName,
            )}
          />

          <div
            id="card-field"
            className="relative mt-2 rounded-field bg-secondary h-14 p-sm focused-container w-full border-2 border-transparent ring-silver-700 ring-1 focus-visible:ring-silver-700 focus-visible:ring-1 shadow-on-focus"
            ref={cardFieldDivRef}
            {...getQaTestingProps(QaTestingElements.CardField)}
          />

          <div className={cn(loading ? 'hidden' : 'flex mt-md space-x-2')}>
            {navigation}
            <PayButton
              onClick={() =>
                submitCardForm(nuveiCardFieldRef.current, cardHolderName)
              }
              title={
                PaymentStore.flow === PaymentIntentFlow.Manage
                  ? t('Add new card')
                  : t('Proceed to pay')
              }
              disabled={!isCardHolderFieldValid || !isCardNumberFieldComplete}
            />
          </div>
        </div>
      )}
    </div>
  );
}
